
export const resolveHistoryLocale = (langMode) => {
  if (langMode === "ar") return "ar";
  if (langMode === "ur") return "ur";
  return "en-US";
};

export const formatEventTimestamp = (value, langMode) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(resolveHistoryLocale(langMode), {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatEnumLabel = (value) => {
  if (!value) return "";
  return String(value)
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
};

// The API is expected to return newest-first, but sort defensively rather
// than trust that ordering -- the meter summary card depends on it.
export const sortEventsByTimestampDesc = (events) =>
  [...(events || [])].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

export const latestHistorySnapshot = (events) => {
  const latest = (events || []).find((event) => event.eventType === "HISTORY" && event.newValue);
  return latest?.newValue || {};
};

export const uniquePerformerIds = (events) => {
  const ids = new Set();
  (events || []).forEach((event) => {
    const id = event.performedBy ?? event.changedBy;
    if (id != null) ids.add(id);
  });
  return Array.from(ids);
};

const EVENT_TONE_BY_KEY = {
  RECEIVE: "blue",
  INSTALL: "green",
  REMOVE: "red",
  UPDATE: "amber",
  CREATE: "teal",
  ASSIGN: "purple",
  TRANSFER: "blue",
};

export const eventTone = (event) => {
  if (event.eventType === "SEAL_USED") return "green";
  const key = event.transactionType || event.action;
  return EVENT_TONE_BY_KEY[key] || "slate";
};

export const eventTitle = (event, t) => {
  if (event.eventType === "SEAL_USED") {
    return event.sealNumber
      ? t.inventoryHistorySealUsedEvent.replace("{seal}", event.sealNumber)
      : t.inventorySealsFitted;
  }
  if (event.eventType === "TRANSACTION") {
    const map = {
      RECEIVE: t.inventoryHistoryReceivedEvent,
      INSTALL: t.inventoryStatusInstalled,
      REMOVE: t.inventoryStatusRemoved,
    };
    return (
      map[event.transactionType] || formatEnumLabel(event.transactionType) || t.inventoryHistoryAuditEvent
    );
  }
  const map = {
    CREATE: t.inventoryHistoryRecordCreated,
    UPDATE: t.inventoryUpdated,
  };
  return map[event.action] || formatEnumLabel(event.action) || t.inventoryHistoryAuditEvent;
};


const shallowDiffRows = (oldObj = {}, newObj = {}) => {
  const safeOld = oldObj || {};
  const safeNew = newObj || {};
  const keys = Array.from(new Set([...Object.keys(safeOld), ...Object.keys(safeNew)]));
  return keys
    .filter((key) => String(safeOld[key] ?? "") !== String(safeNew[key] ?? ""))
    .map((key) => ({
      field: key,
      oldValue: safeOld[key] ?? "—",
      newValue: safeNew[key] ?? "—",
    }));
};

const HIDDEN_HISTORY_FIELDS = new Set(["storeId", "assignedTo", "projectId"]);

export const buildFieldDiff = (event) => {
  if (event.eventType !== "HISTORY" || !event.changedFields?.length) return [];
  const oldVal = event.oldValue || {};
  const newVal = event.newValue || {};
  const rows = [];
  event.changedFields.forEach((field) => {
    if (field === "metadata") {
      // Nested object -- diff its own keys directly rather than prefixing
      // each row with "metadata.", which reads as internal plumbing in the UI.
      rows.push(...shallowDiffRows(oldVal.metadata, newVal.metadata));
      return;
    }
    // List fields (sealNumbers) read better comma-separated than as String(array).
    const asText = (value) =>
      Array.isArray(value) ? (value.length ? value.join(", ") : null) : value;
    const ov = asText(oldVal[field]);
    const nv = asText(newVal[field]);
    if (String(ov ?? "") === String(nv ?? "")) return;
    rows.push({ field, oldValue: ov ?? "—", newValue: nv ?? "—" });
  });
  return rows.filter((row) => !HIDDEN_HISTORY_FIELDS.has(row.field));
};

export const eventTagData = (event) => ({
  statusTransition:
    event.fromStatus && event.toStatus ? { from: event.fromStatus, to: event.toStatus } : null,
  locationMove:
    event.fromLocation && event.toLocation && event.fromLocation !== event.toLocation
      ? { from: event.fromLocation, to: event.toLocation }
      : event.toLocation && !event.fromLocation
        ? { to: event.toLocation }
        : null,
  accountId: event.accountId || event.accountNo || null,
  // SEAL_USED events name the meter the seal was fitted on.
  meterIdentifier: event.eventType === "SEAL_USED" ? event.meterIdentifier || null : null,
  taskId: event.taskId || null,
  replacementId: event.replacementId || null,
  reference: event.referenceType ? { type: event.referenceType, id: event.referenceId } : null,
  itemType: event.itemType || null,
  sealNumbers: Array.isArray(event.sealNumbers) && event.sealNumbers.length ? event.sealNumbers : null,
  note: event.note || event.reason || null,
  performerId: event.performedBy ?? event.changedBy ?? event.usedBy ?? null,
  // SEAL_USED events already carry the fitter's name, so no user lookup is needed.
  performerName: event.usedByName || null,
  source: event.source || null,
});
